import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header.jsx';
import { useRecordatorios, useRecordatoriosHoy, useUpdateRecordatorio } from '../features/recordatorios/useRecordatorios.js';
import { formatFecha } from '../lib/formatters.js';

const TABS = [
  { key: 'activos',   label: 'Activos',   estados: ['activo', 'vencido'] },
  { key: 'resueltos', label: 'Resueltos', estados: ['resuelto', 'reprogramado'] },
];

const ESTADO_STYLE = {
  activo:       'bg-blue-100 text-blue-700',
  vencido:      'bg-red-100 text-red-700',
  resuelto:     'bg-green-100 text-green-700',
  reprogramado: 'bg-yellow-100 text-yellow-700',
};

function agruparPorLead(recordatorios) {
  const mapa = {};
  for (const r of recordatorios) {
    if (!mapa[r.lead_id]) mapa[r.lead_id] = { lead: r.lead, lead_id: r.lead_id, items: [] };
    mapa[r.lead_id].items.push(r);
  }
  // Ordenar items de cada grupo por fecha_compromiso asc
  for (const g of Object.values(mapa)) {
    g.items.sort((a, b) => new Date(a.fecha_compromiso) - new Date(b.fecha_compromiso));
  }
  return Object.values(mapa);
}

export default function RecordatoriosPage() {
  const navigate = useNavigate();
  const { data: recordatorios = [], isLoading } = useRecordatorios();
  const { data: hoy = [] } = useRecordatoriosHoy();

  const [tab, setTab] = useState('activos');
  const [abiertos, setAbiertos] = useState(new Set());
  const [popupVisible, setPopupVisible] = useState(false);
  const popupShown = useRef(false);

  useEffect(() => {
    if (hoy.length === 0 || popupShown.current) return;
    const hoyStr = new Date().toISOString().slice(0, 10);
    const visto = localStorage.getItem('recordatorios_popup_fecha');
    if (visto !== hoyStr) {
      setPopupVisible(true);
      localStorage.setItem('recordatorios_popup_fecha', hoyStr);
    }
    popupShown.current = true;
  }, [hoy]);

  function toggleGrupo(lead_id) {
    setAbiertos((prev) => {
      const next = new Set(prev);
      next.has(lead_id) ? next.delete(lead_id) : next.add(lead_id);
      return next;
    });
  }

  const estadosTab = TABS.find((t) => t.key === tab).estados;
  const filtrados  = recordatorios.filter((r) => estadosTab.includes(r.estado));
  const grupos     = agruparPorLead(filtrados);

  return (
    <div>
      <Header title="Recordatorios" />

      {popupVisible && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4 max-h-[85vh] flex flex-col gap-4">
            <h2 className="font-['Raleway'] font-bold text-[#0D2B5C] text-lg shrink-0">
              Recordatorios de hoy ({hoy.length})
            </h2>
            <ul className="flex flex-col gap-2 overflow-y-auto flex-1 min-h-0 pr-1">
              {hoy.map((r) => (
                <li key={r.id} className="font-['Inter'] text-sm text-[#0D2B5C]">
                  <span className="font-medium">{r.lead?.nombre_apellido}</span>
                  {r.nota && <span className="text-[#6B7280]"> — {r.nota}</span>}
                </li>
              ))}
            </ul>
            <button
              onClick={() => setPopupVisible(false)}
              className="bg-[#1E9BE8] hover:bg-[#1780C5] text-white font-['Raleway'] font-bold text-sm px-6 py-3 rounded-md transition-colors min-h-[44px] shrink-0"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      <div className="p-6 flex flex-col gap-4">

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200">
          {TABS.map((t) => {
            const count = recordatorios.filter((r) => t.estados.includes(r.estado)).length;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-5 py-2.5 font-['Raleway'] font-semibold text-sm transition-colors border-b-2 -mb-px ${
                  tab === t.key
                    ? 'border-[#0D2B5C] text-[#0D2B5C]'
                    : 'border-transparent text-[#6B7280] hover:text-[#0D2B5C]'
                }`}
              >
                {t.label}
                {count > 0 && (
                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    tab === t.key ? 'bg-[#0D2B5C] text-white' : 'bg-gray-100 text-[#6B7280]'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Grupos */}
        {isLoading ? (
          <p className="font-['Inter'] text-[#6B7280]">Cargando...</p>
        ) : grupos.length === 0 ? (
          <p className="font-['Inter'] text-[#6B7280]">No hay recordatorios {tab}.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {grupos.map(({ lead, lead_id, items }) => {
              const proximo = items[0];
              const abierto = abiertos.has(lead_id);
              const tieneVencido = items.some((r) => r.estado === 'vencido');

              return (
                <div key={lead_id} className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">

                  {/* Header colapsable */}
                  <div
                    onClick={() => toggleGrupo(lead_id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50 ${tieneVencido ? 'bg-red-50 hover:bg-red-100' : 'bg-gray-50'}`}
                  >
                    {/* Chevron */}
                    <svg
                      className={`w-4 h-4 text-[#6B7280] shrink-0 transition-transform ${abierto ? 'rotate-90' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>

                    {/* Nombre del lead */}
                    <span className="flex-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/leads/${lead_id}`); }}
                        className="font-['Raleway'] font-semibold text-[#0D2B5C] hover:text-[#1E9BE8] transition-colors text-sm text-left"
                      >
                        {lead?.nombre_apellido ?? '—'}
                      </button>
                    </span>

                    {/* Próximo recordatorio */}
                    <span className="font-['Inter'] text-xs text-[#6B7280] shrink-0">
                      Próx: {formatFecha(proximo.fecha_compromiso)}
                    </span>

                    {/* Badge estado del próximo */}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${ESTADO_STYLE[proximo.estado] ?? 'bg-gray-100 text-gray-700'}`}>
                      {proximo.estado}
                    </span>

                    {/* Contador */}
                    {items.length > 1 && (
                      <span className="text-xs font-['Inter'] text-[#6B7280] bg-gray-100 px-1.5 py-0.5 rounded-full shrink-0">
                        {items.length}
                      </span>
                    )}
                  </div>

                  {/* Detalle expandido */}
                  {abierto && (
                    <table className="w-full text-sm font-['Inter'] border-t border-gray-100">
                      <tbody>
                        {items.map((r) => (
                          <RecordatorioRow key={r.id} r={r} />
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function RecordatorioRow({ r }) {
  const update = useUpdateRecordatorio(r.id);
  const [reprogramando, setReprogramando] = useState(false);
  const [nuevaFecha, setNuevaFecha] = useState('');

  function handleReprogramar() {
    if (!nuevaFecha) return;
    update.mutate({ estado: 'reprogramado', fecha_compromiso: nuevaFecha }, {
      onSuccess: () => { setReprogramando(false); setNuevaFecha(''); },
    });
  }

  return (
    <tr className={`border-b border-gray-50 last:border-0 ${r.estado === 'vencido' ? 'bg-red-50' : ''}`}>
      <td className="px-4 py-3 text-[#6B7280] w-32 shrink-0">{formatFecha(r.fecha_compromiso)}</td>
      <td className="px-4 py-3 w-28">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_STYLE[r.estado] ?? 'bg-gray-100 text-gray-700'}`}>
          {r.estado}
        </span>
      </td>
      <td className="px-4 py-3 text-[#6B7280]">{r.nota ?? '—'}</td>
      <td className="px-4 py-3 text-right">
        {reprogramando ? (
          <div className="flex items-center justify-end gap-2">
            <input
              type="date"
              value={nuevaFecha}
              onChange={(e) => setNuevaFecha(e.target.value)}
              className="border border-gray-200 rounded px-2 py-1 text-xs font-['Inter'] focus:outline-none focus:ring-1 focus:ring-[#0D2B5C]"
            />
            <button
              onClick={handleReprogramar}
              disabled={!nuevaFecha || update.isPending}
              className="text-[#0D2B5C] hover:text-[#1E9BE8] font-['Raleway'] font-semibold text-xs transition-colors disabled:opacity-40"
            >
              Guardar
            </button>
            <button
              onClick={() => setReprogramando(false)}
              className="text-[#6B7280] hover:text-[#0D2B5C] font-['Raleway'] text-xs transition-colors"
            >
              Cancelar
            </button>
          </div>
        ) : (
          <div className="flex justify-end gap-3">
            {r.estado === 'activo' && (
              <>
                <button
                  onClick={() => update.mutate({ estado: 'resuelto' })}
                  className="text-[#0D2B5C] hover:text-[#1E9BE8] font-['Raleway'] font-semibold text-xs transition-colors"
                >
                  Resolver
                </button>
                <button
                  onClick={() => setReprogramando(true)}
                  className="text-[#6B7280] hover:text-[#0D2B5C] font-['Raleway'] text-xs transition-colors"
                >
                  Reprogramar
                </button>
              </>
            )}
            {r.estado === 'vencido' && (
              <button
                onClick={() => setReprogramando(true)}
                className="text-red-600 hover:text-red-800 font-['Raleway'] text-xs transition-colors"
              >
                Reprogramar
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
